/* frontend/src/components/SkillLeaderboard.jsx
   ------------------------------------------------------------------
   Real-time chip leaderboard for the 8 early-stage skill candidates —
   replaces IdeaPoll.jsx. Same row language IdeaPoll used (name, tagline,
   vote count, pill vote/voted/loading button) carried over; the data
   layer is what was wrong there, not the visuals: IdeaPoll tallied
   skill_requests client-side, which RLS now scopes to the viewer's own
   rows (0 or 1, never the real count). This reads skill_candidates.votes
   instead, which a DB trigger on skill_requests keeps in sync — real
   cross-user totals, no per-row RLS problem.

   Skills page only — Dashboard just links here (see VoteForNextSection),
   so there is exactly one postgres_changes subscription to this table
   at any time, not one per page.
*/
import { useState, useEffect, useRef, useCallback } from 'react';
import { ThumbsUp, CheckCircle, Users, ShieldAlert, Presentation, Ear, HelpCircle, Compass, Eye, Brain, Sparkles } from 'lucide-react';
import { supabase } from '@services/api.js';
import { useVote } from '@hooks/useVote';
import { useFlip } from '@hooks/useFlip';

// One fixed icon + colour per candidate — the icon replaces the old
// rank-number badge, and the colour replaces the shared orange accent
// once a candidate is voted (see LeaderboardChip below). Frontend-only
// map for now, no migration. Sparkles + the old orange are the fallback
// for any future skill_candidates row not in this map, so a new
// candidate renders something instead of crashing.
const CANDIDATE_ICONS = {
  'family-table': { icon: Users, color: '#C2647A' },
  'hard-conversations': { icon: ShieldAlert, color: '#B0563A' },
  'pitch-craft': { icon: Presentation, color: '#C08419' },
  'deep-listening': { icon: Ear, color: '#2E8B7A' },
  'socratic-questioning': { icon: HelpCircle, color: '#6F63C0' },
  'coaching-mentorship': { icon: Compass, color: '#6F8F3F' },
  'perception-editing': { icon: Eye, color: '#8E5D8C' },
  'learning-edge': { icon: Brain, color: '#3A7CA8' },
};

const sortCandidates = (list) =>
  [...list].sort((a, b) => b.votes - a.votes || a.name.localeCompare(b.name));

/** Competition ranking — ties share a rank, the next distinct vote count
 * skips accordingly (two candidates on 3 votes are both rank 2; the next
 * candidate is rank 4, not 3). Requires `list` already sorted. */
const withRanks = (sorted) => {
  let rank = 0;
  let prevVotes = null;
  return sorted.map((c, i) => {
    if (c.votes !== prevVotes) { rank = i + 1; prevVotes = c.votes; }
    return { ...c, rank };
  });
};

const fetchCandidates = async () => {
  const { data, error } = await supabase
    .from('skill_candidates')
    .select('id, name, tagline, votes, display_order')
    .eq('is_active', true);
  if (error) throw error;
  return withRanks(sortCandidates(data ?? []));
};

const LeaderboardChip = ({ candidate, isDark, headColor, mutedColor, source, registerNode, justChanged }) => {
  const { voted, loading, error, vote } = useVote(candidate.id, source);
  const candidateMeta = CANDIDATE_ICONS[candidate.id];
  const accent = candidateMeta?.color ?? '#ea580c';
  const Icon = candidateMeta?.icon ?? Sparkles;

  return (
    <div
      ref={registerNode(candidate.id)}
      data-testid={`leaderboard-chip-${candidate.id}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px', borderRadius: 12,
        background: isDark ? '#1c1917' : '#ffffff',
        border: `1px solid ${isDark ? '#2d2825' : '#e8e4df'}`,
      }}
    >
      {/* Icon and its box size/position never change — only background
          and color, which is a paint-only style change safe for FLIP to
          measure around. Driven solely by voted state now; the old
          rank === 1 tint is gone; it collided with the per-candidate
          voted colour and rank is already visible from position + count. */}
      <div style={{
        flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: voted ? `${accent}21` : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
        color: voted ? accent : mutedColor,
        transition: 'background-color 0.25s ease, color 0.25s ease',
      }}>
        <Icon size={13} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: headColor }}>{candidate.name}</span>
          <span
            className={justChanged ? 'chip-vote-pulse' : ''}
            style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, color: mutedColor, '--pulse-color': accent }}
          >
            {candidate.votes} {candidate.votes === 1 ? 'vote' : 'votes'}
          </span>
        </div>
        <p style={{ fontSize: 12, color: mutedColor, margin: '3px 0 0', lineHeight: 1.5 }}>{candidate.tagline}</p>
        {error && <p style={{ fontSize: 11, color: '#dc2626', margin: '4px 0 0' }}>{error}</p>}
      </div>

      <button
        onClick={vote}
        disabled={loading}
        style={{
          flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6,
          padding: '7px 12px', borderRadius: 99, fontSize: 11, fontWeight: 700,
          border: `1px solid ${voted ? accent : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)')}`,
          background: voted ? `${accent}15` : 'transparent',
          color: voted ? accent : mutedColor,
          cursor: 'pointer',
          transition: 'all 0.18s ease',
        }}
      >
        {loading
          ? <div style={{ width: 11, height: 11, border: `2px solid ${accent}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'leaderboard-spin 0.7s linear infinite' }} />
          : voted
          ? <><CheckCircle size={12} />Voted</>
          : <><ThumbsUp size={12} />Vote</>}
      </button>
    </div>
  );
};

/** Bare chip list — no heading/description of its own. Rendered inside
 * VoteForNextSection, which owns the "Vote for what's next" heading
 * shared with the Translation announcement above it. */
export const SkillLeaderboard = ({ isDark, headColor, mutedColor, source }) => {
  const [candidates, setCandidates] = useState([]);
  const [ready, setReady] = useState(false);
  const [justChangedIds, setJustChangedIds] = useState(() => new Set());
  const prevVotesRef = useRef(new Map());
  const { registerNode, prepare } = useFlip();

  const applyAndSort = useCallback((list) => {
    prepare(); // capture First rects before the reorder commits
    setCandidates(withRanks(sortCandidates(list)));
  }, [prepare]);

  const refetch = useCallback(async () => {
    try {
      const data = await fetchCandidates();
      applyAndSort(data);
    } catch {
      // leave whatever's currently shown — a failed refetch shouldn't blank the board
    } finally {
      setReady(true);
    }
  }, [applyAndSort]);

  useEffect(() => { refetch(); }, [refetch]);

  // Realtime — replace the count with the payload's value, never
  // increment it. The voter's own tap already applies via useVote's
  // optimistic state on their own chip's button; the leaderboard's count
  // itself only ever moves from a payload. Incrementing on the echo of
  // your own vote would double-count it.
  useEffect(() => {
    const channel = supabase
      .channel('skill_candidates_votes')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'skill_candidates' }, (payload) => {
        prepare();
        setCandidates((prev) => {
          const target = prev.find((c) => c.id === payload.new.id);
          // Bails on a no-op value — also covers the echo of the user's
          // own vote arriving after it's already applied optimistically.
          if (!target || target.votes === payload.new.votes) return prev;
          const next = prev.map((c) =>
            c.id === payload.new.id ? { ...c, votes: payload.new.votes } : c
          );
          return withRanks(sortCandidates(next));
        });
      })
      .subscribe((status) => {
        // Fires on the initial subscribe AND on every reconnect — Realtime
        // drops events during sleep, so a reconnect must resync the full
        // list rather than trust whatever arrived (or didn't) meanwhile.
        if (status === 'SUBSCRIBED') refetch();
      });

    const onFocus = () => refetch();
    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener('focus', onFocus);
      supabase.removeChannel(channel);
    };
  }, [refetch, prepare]);

  // Per-chip vote-count pulse, independent of any reordering.
  useEffect(() => {
    const changed = new Set();
    candidates.forEach((c) => {
      const prev = prevVotesRef.current.get(c.id);
      if (prev != null && prev !== c.votes) changed.add(c.id);
      prevVotesRef.current.set(c.id, c.votes);
    });
    if (changed.size > 0) {
      setJustChangedIds(changed);
      const t = setTimeout(() => setJustChangedIds(new Set()), 600);
      return () => clearTimeout(t);
    }
  }, [candidates]);

  if (!ready) return null;

  return (
    <div>
      <style>{`
        @keyframes leaderboard-spin { to { transform: rotate(360deg); } }
        @keyframes chip-vote-pulse { 0%, 100% { color: ${mutedColor}; } 50% { color: var(--pulse-color, #ea580c); } }
        .chip-vote-pulse { animation: chip-vote-pulse 0.6s ease-in-out; }
        @media (prefers-reduced-motion: reduce) { .chip-vote-pulse { animation: none; } }
      `}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {candidates.map((c) => (
          <LeaderboardChip
            key={c.id}
            candidate={c}
            isDark={isDark}
            headColor={headColor}
            mutedColor={mutedColor}
            source={source}
            registerNode={registerNode}
            justChanged={justChangedIds.has(c.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default SkillLeaderboard;
